import { Box, Card, Container, Typography, useMediaQuery } from "@mui/material";
import { FC, useEffect } from "react";
import InfoBoxGroup from "../base/InfoBoxGroup";
import { useDispatch, useSelector } from "../../store";
import { fetchApplicationList } from "../../slices/application";
import theme from "../../theme";
import Button from "../base/Button";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import checkRole, { UserRoles } from "../../helpers/checkUserRoles";
import { startTrial } from "../../slices/company";
import getPermissions from "../../helpers/getAppPermissions";

const Apps: FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { enabledApplications } = useSelector((store) => store.application);
  const { selectedCompany, status, licenses } = useSelector(
    (store) => store.company,
  );
  const { dbUser } = useSelector((store) => store.auth);
  const { savedRoute, appLoaded } = useSelector((store) => store.shared);

  const md = useMediaQuery(theme.breakpoints.up("md"));
  const sm = useMediaQuery(theme.breakpoints.up("sm"));
  const hasAnyTrial = licenses?.some((license) => license.isTrial) ?? false;
  const hasAnyPurchase =
    licenses?.some((license) => !!license.purchaseDate) ?? false;

  useEffect(() => {
    if (
      !!selectedCompany?.finishedSetup &&
      !!dbUser &&
      enabledApplications?.length < 1 &&
      !appLoaded.appList
    ) {
      dispatch(fetchApplicationList());
    }
  }, [
    dispatch,
    selectedCompany?.finishedSetup,
    dbUser,
    enabledApplications?.length,
    appLoaded.appList,
  ]);

  useEffect(() => {
    if (
      enabledApplications?.length === 1 &&
      !savedRoute &&
      window.location.pathname === "/apps"
    ) {
      const app = enabledApplications[0];
      window.location.href = app.url;
    }
  }, [enabledApplications, navigate, savedRoute]);

  const applications = enabledApplications?.map((app) => {
    const appLicense = licenses?.find((x) => x.product.id === app.id);

    const daysRemaining = !!appLicense
      ? dayjs(appLicense?.trialEndDate).diff(dayjs(new Date()), "day")
      : 0;
    const hasPurchase = !!appLicense?.purchaseDate;
    const isTrialActive = !!appLicense?.isTrial && daysRemaining > 0;
    const trialExpired = !!appLicense?.isTrial && !isTrialActive;
    const canContinue = !!appLicense && (hasPurchase || isTrialActive);
    const showIndividualStartTrial = hasAnyTrial && !appLicense;

    return (
      <Card
        key={app.id}
        sx={{
          gap: 3,
          height: { xs: "430px", sm: "350px", md: "360px" },
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            md: "repeat(auto-fit, minmax(200px, 1fr))",
          },
          justifyContent: "center",
          alignItems: "center",
          backgroundColor: theme.palette.background.default,
          paddingY: { xs: 5, md: 5 },
          paddingX: { xs: 2, md: 7 },
          borderRadius: 2,
          width: "100%",
        }}
      >
        {!app.enabled && <></>}
        <Box
          display="flex"
          flexDirection="column"
          sx={{ flexShrink: 0, alignItems: "center", justifyContent: "center" }}
          color={theme.palette.primary.main}
        >
          <Box style={{ height: "100%", objectFit: "cover" }}>
            <img
              height={"100%"}
              width={md ? 400 : sm ? 230 : 200}
              src={app.logo?.url}
              alt={app.name}
              style={{
                marginTop: "-80px",
                marginBottom: "-80px",
                objectFit: "cover",
              }}
            />
          </Box>
        </Box>
        <Box
          display="flex"
          flexDirection="column"
          height="100%"
          marginTop={1}
          gap={{ xs: 4, sm: 2, md: 3 }}
          sx={{
            alignItems: "center",
            justifyContent: "space-around",
            flexGrow: 1,
            flexShrink: 1,
            minWidth: "0",
            wordWrap: "break-word",
            overflowWrap: "break-word",
            wordBreak: "break-word",
          }}
        >
          <Box display="flex" flexDirection="row">
            <Typography variant="h4" fontWeight="bold">
              {app.title}
            </Typography>
          </Box>
          <Box display="flex" flexDirection="row">
            <Typography
              variant={md ? "h4" : sm ? "h5" : "body1"}
              textAlign={{ xs: "center", md: "left" }}
            >
              {app.description}
            </Typography>
          </Box>
          <Box
            display="flex"
            flexDirection="row"
            width="100%"
            justifyContent="center"
          >
            <Box
              display="flex"
              flexDirection="column"
              justifyContent="center"
              alignItems="center"
              gap={2}
            >
              {checkRole(dbUser!, UserRoles.GlobalAdmin) &&
                !!appLicense &&
                !appLicense.purchaseDate &&
                app.enabled && (
                  <Typography variant="h5">
                    {isTrialActive
                      ? `${daysRemaining} Days Remaining in Trial`
                      : trialExpired
                        ? "Trial expired. Purchase product to continue."
                        : "Purchase Product to Continue"}
                  </Typography>
                )}
              {canContinue ? (
                <Button
                  label={
                    app.enabled ? `Continue to ${app.name}` : "Coming Soon!"
                  }
                  variant={"contained"}
                  color={"primary"}
                  onClick={() => {
                    window.location.href = `${app.url}`;
                  }}
                  loading={status === "loading"}
                  disabled={
                    !app.enabled ||
                    !getPermissions(dbUser!, UserRoles.AnyAdmin, appLicense)
                  }
                />
              ) : (
                showIndividualStartTrial && (
                  <Box
                    display="flex"
                    flexDirection="row"
                    width="100%"
                    justifyContent="center"
                    marginBottom={2}
                  >
                    <Box
                      display="flex"
                      flexDirection="column"
                      justifyContent="center"
                    >
                      <Button
                        label={
                          app.enabled ? "Start Free Trial!" : "Coming Soon!"
                        }
                        variant={"contained"}
                        color={"success"}
                        onClick={() => {
                          dispatch(
                            startTrial({
                              id: selectedCompany?.id!,
                              appId: app.id,
                            }),
                          );
                        }}
                        disabled={
                          !selectedCompany ||
                          !app.enabled ||
                          !checkRole(dbUser!, UserRoles.AnyAdmin)
                        }
                      />
                    </Box>
                  </Box>
                )
              )}
            </Box>
          </Box>
        </Box>
      </Card>
    );
  });

  return (
    <Container sx={{ marginBottom: 8 }}>
      {checkRole(dbUser!, UserRoles.GlobalAdmin) &&
        status !== "loading" &&
        !!selectedCompany &&
        !hasAnyTrial &&
        !hasAnyPurchase && (
          <Box
            display="flex"
            flexDirection="row"
            width="100%"
            justifyContent="center"
            marginBottom={2}
          >
            <Box display="flex" flexDirection="column" justifyContent="center">
              <Button
                label="Start Free Trial!"
                variant={"contained"}
                color={"success"}
                onClick={() => {
                  dispatch(startTrial({ id: selectedCompany?.id! }));
                }}
                disabled={!selectedCompany}
              />
            </Box>
          </Box>
        )}
      <InfoBoxGroup rows content={applications} />
    </Container>
  );
};

export default Apps;
